// Pure helpers for the ZONA for brands page, kept apart so they can be tested.

// Body for POST /brand/admin/events/:eventId/decide. Unavailable people are never approved,
// ids are deduped, and anyone in both lists is left out of both.
export function buildDecidePayload(invites, { approve = [], reject = [] } = {}) {
  const known = new Map((invites || []).map((i) => [String(i.user_id), i]));
  const clean = (ids) => [...new Set((ids || []).map(String))].filter((id) => known.has(id));
  const rej = new Set(clean(reject));
  const app = clean(approve).filter((id) => !known.get(id).unavailable);
  const both = new Set(app.filter((id) => rej.has(id)));
  return {
    approve: app.filter((id) => !both.has(id)),
    reject: [...rej].filter((id) => !both.has(id)),
  };
}

// "Approve all": everyone in the batch who can still be invited.
export const approveAllIds = (invites) =>
  (invites || []).filter((i) => !i.unavailable).map((i) => String(i.user_id));

const STATUS_ORDER = { pending: 0, approved: 1, declined: 2, removed: 3 };
export const sortBrands = (brands) =>
  [...(brands || [])].sort(
    (a, b) => (STATUS_ORDER[a.brand_account?.status] ?? 9) - (STATUS_ORDER[b.brand_account?.status] ?? 9),
  );

// Only https links open from this page.
export const safeHttps = (url) => (typeof url === "string" && /^https:\/\/\S+$/i.test(url.trim()) ? url.trim() : null);

const HANDLE = /^[A-Za-z0-9._]{1,40}$/;
const HOSTS = { instagram: /^(www\.)?instagram\.com$/i, tiktok: /^(www\.)?tiktok\.com$/i };

// A link only for a plain handle or an instagram.com / tiktok.com https URL; anything else is text.
export function handleLink(platform, raw) {
  if (typeof raw !== "string" || !raw.trim()) return null;
  const v = raw.trim();
  const text = { label: v, url: null };
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) {
    try {
      const u = new URL(v);
      const ok = u.protocol === "https:" && Object.values(HOSTS).some((h) => h.test(u.hostname));
      return ok ? { label: v.replace(/^https:\/\/(www\.)?/i, ""), url: u.href } : text;
    } catch (_) {
      return text;
    }
  }
  const h = v.replace(/^@/, "");
  if (!HANDLE.test(h)) return text;
  if (platform === "instagram") return { label: `@${h}`, url: `https://www.instagram.com/${h}` };
  if (platform === "tiktok") return { label: `@${h}`, url: `https://www.tiktok.com/@${h}` };
  return text;
}

export const TIER_LABELS = { guest_list: "Guest list", activation: "Activation", full_night: "Full night" };
export const PLATFORM_LABELS = { instagram: "Instagram", tiktok: "TikTok", youtube: "YouTube", x: "X" };

export const wordLabel = (s) => {
  const t = String(s || "").replace(/_/g, " ");
  return t.charAt(0).toUpperCase() + t.slice(1);
};

// Rounds first, then picks the unit, so 999,950 reads 1M rather than 1000K.
const UNITS = ["", "K", "M", "B"];
export function compactCount(n) {
  if (!Number.isFinite(n)) return "";
  let v = n;
  for (let i = 0; ; i += 1) {
    const r = i === 0 ? Math.round(v) : Number(v.toFixed(Math.abs(v) >= 10 ? 0 : 1));
    if (Math.abs(r) < 1000 || i === UNITS.length - 1) return `${r}${UNITS[i]}`;
    v /= 1000;
  }
}
